import { useEffect, useRef, useState } from 'react';
import {
  api,
  CommissioningOcrExtraction,
  CommissioningRecord,
  ExistingCommission,
  ParentAssetMatch,
  PendingCommissioning,
} from '../../api/client';
import { parseDbTimestamp } from '../../lib/parseDbTimestamp';
import { getBridgeStatus, tapAndAudit, NfcBridgeStatus } from '../../lib/nfcBridge';
import { Badge, Alert } from '../../components/ui';
import { AlreadyCommissionedNotice, alreadyCommissioned } from './RetireCommission';
import { BuildingIcon, ParentAssetPicker, RealmPill, SearchIcon, Spinner } from '../../components/ParentAssetPicker';
import type { BadgeTone } from '../../components/ui';
import { DownloadOriginalButton } from '../../components/DownloadOriginalButton';

// Option #2 commissioning: the worker creates the Portal asset (full attribute
// schema + DEFAULT_ATTRIBUTE_META, including storeDataPoints=true) but does
// NOT touch METERS_KV / ROUTES_KV / M2M_KV / Frontdoor / modem registration.
// That stays human-gated, so every freshly committed row gets flagged
// `needs_cf_wiring=1` and shows up in the follow-up list below until a human
// runs scripts/openremote-create-consumer-asset.py (or wires KV manually) and
// flips the flag via "Mark CF-wired".

const ASSET_TYPES: Array<{ value: string; label: string; meterFamily: 'electricity' | 'water' }> = [
  { value: 'ElectricityConsumerAsset', label: 'Electricity Consumer (Modem schema)', meterFamily: 'electricity' },
  { value: 'GroundwaterSensorAsset', label: 'Groundwater Sensor (Axioma water)', meterFamily: 'water' },
];

type Result =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | {
      kind: 'success';
      asset_id: string;
      record_id: string;
      needs_cf_wiring: boolean;
      warnings: string[];
    }
  /** `existing` is set when finalize refused because the serial is already
   *  committed: the notice then offers to retire that commission. */
  | { kind: 'error'; message: string; existing?: ExistingCommission };

export default function CommissionPage() {
  const [meterSerial, setMeterSerial] = useState('');
  const [assetType, setAssetType] = useState(ASSET_TYPES[0].value);
  const [parentId, setParentId] = useState('');
  // Mains breaker rating — electricity meters only. Free-text input so the
  // field tech can type "60" or "100" off the nameplate; phases default to 3
  // (almost all commercial installs in SA are 3-phase). Stored on
  // meter_config + pushed into Portal as the breaker_size attribute value.
  const [breakerSizeAmps, setBreakerSizeAmps] = useState('');
  const [breakerPhases, setBreakerPhases] = useState<'3' | '1'>('3');
  const [ocr, setOcr] = useState<CommissioningOcrExtraction | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [imageR2Key, setImageR2Key] = useState<string | null>(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [result, setResult] = useState<Result>({ kind: 'idle' });
  const [showFormParentPicker, setShowFormParentPicker] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // NFC bridge: same probe as ReadingPage. The NFC tap can fill in
  // chip UID and (for QW1) auto-select the water asset type. The
  // meter serial still has to be typed/scanned by the tech for v1
  // since passive memory doesn't carry it.
  const [bridgeStatus, setBridgeStatus] = useState<NfcBridgeStatus>('offline');
  const [nfcBusy, setNfcBusy] = useState(false);
  const [nfcError, setNfcError] = useState<string | null>(null);
  const [nfcChipUid, setNfcChipUid] = useState<string | null>(null);
  useEffect(() => {
    getBridgeStatus().then(setBridgeStatus);
  }, []);

  async function handleNfc() {
    setNfcBusy(true);
    setNfcError(null);
    try {
      const event = await tapAndAudit({
        meterSerialHint: meterSerial.trim() || undefined,
      });
      setNfcChipUid(event.meter.chip_uid);
      // NFC for QW1 implies water — flip the asset-type card.
      if (event.meter.device_type === 'water') {
        setAssetType('GroundwaterSensorAsset');
      } else if (event.meter.device_type === 'electricity') {
        setAssetType('ElectricityConsumerAsset');
      }
      // The bridge passes the meter_serial back as a pass-through of
      // what we sent. If the user hadn't typed one, leave it blank for
      // them to fill from the meter face.
      if (event.meter.serial && !meterSerial.trim()) {
        setMeterSerial(event.meter.serial);
      }
    } catch (err) {
      setNfcError((err as Error).message);
    } finally {
      setNfcBusy(false);
    }
  }

  async function handleOcr(file: File | null) {
    if (!file) return;
    setOcrBusy(true);
    setOcrError(null);
    try {
      const r = await api.ocrCommissioning(file);
      setOcr(r.extracted);
      setImageId(r.image_id);
      setImageR2Key(r.image_r2_key);
      if (r.extracted.meter_serial) setMeterSerial(r.extracted.meter_serial);
      if (r.extracted.meter_type === 'electricity') setAssetType('ElectricityConsumerAsset');
      else if (r.extracted.meter_type === 'water') setAssetType('GroundwaterSensorAsset');
    } catch (err) {
      setOcrError((err as Error).message);
    } finally {
      setOcrBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await submit();
  }

  async function submit() {
    if (!meterSerial.trim() || !assetType || !parentId.trim()) return;
    setResult({ kind: 'submitting' });
    try {
      const isElec = ASSET_TYPES.find(t => t.value === assetType)?.meterFamily === 'electricity';
      const breakerNum = breakerSizeAmps.trim() ? Number(breakerSizeAmps.trim()) : undefined;
      const resp = await api.finalizeCommissioning({
        meter_serial: meterSerial.trim(),
        asset_type: assetType,
        parent_id: parentId.trim(),
        source: ocr ? 'ocr' : 'manual',
        source_payload: ocr ?? undefined,
        image_id: imageId ?? undefined,
        image_r2_key: imageR2Key ?? undefined,
        breaker_size_amps: isElec && Number.isFinite(breakerNum) && (breakerNum as number) > 0 ? breakerNum : undefined,
        breaker_phases: isElec ? Number(breakerPhases) : undefined,
      });
      setResult({
        kind: 'success',
        asset_id: resp.asset_id,
        record_id: resp.record_id,
        needs_cf_wiring: resp.needs_cf_wiring,
        warnings: resp.warnings || [],
      });
    } catch (err) {
      setResult({ kind: 'error', message: (err as Error).message, existing: alreadyCommissioned(err) ?? undefined });
    }
  }

  function reset() {
    setMeterSerial('');
    setParentId('');
    setBreakerSizeAmps('');
    setBreakerPhases('3');
    setOcr(null);
    setImageId(null);
    setImageR2Key(null);
    setOcrError(null);
    setResult({ kind: 'idle' });
    if (fileRef.current) fileRef.current.value = '';
  }

  const formValid = meterSerial.trim().length > 0 && parentId.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Not wrapped in <Card>: the border-l-4 colour stripe is a deliberate
          section identifier (blue=commission form, indigo=email queue,
          amber=needs-wiring, see the other two lists below) — Card's own
          `border` on all four sides would fight it, same reasoning group E
          used to keep MeterConfigModal's amber-highlighted calibration box
          out of Card. bg-white -> bg-surface-raised only. */}
      <div className="bg-surface-raised rounded-xl shadow overflow-hidden border-l-4 border-primary-500">
        {/* bg-gradient-to-r from-primary-50/60 is a fixed light wash — left
            alone it renders as a bright patch fading over the dark card.
            dark:from-primary-900/40 (a dark-teal version of the same wash,
            #134e4a — the same hex Badge's success tone uses as its dark
            fill) keeps the "branded header" reading without the flash. */}
        <div className="px-6 py-4 border-b border-border-token bg-gradient-to-r from-primary-50/60 dark:from-primary-900/40 to-transparent">
          <div className="flex items-center gap-2">
            <PlugIcon />
            <h2 className="text-lg font-semibold text-content">Commission a new meter</h2>
          </div>
          <p className="text-sm text-content-muted mt-1">
            Creates the asset in the active realm with the full attribute schema and time-series
            metadata. Cloudflare wiring (METERS_KV / ROUTES_KV / M2M_KV / Frontdoor) is a separate
            human step and shows up below until completed.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Step 1 — optional photo */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              1. Scan nameplate (optional)
            </legend>
            {/* bg-gray-50/60: opacity modifiers don't reliably compile against
                the CSS-var-backed surface tokens (verified empirically for
                `row-selected`, see theme.ts) — dropped in favour of a solid
                bg-surface-sunken. Negligible light-mode difference (stone-50
                at 60%-over-white ≈ #fcfbfa vs surface-sunken's own #faf9f7). */}
            <div className="rounded-lg border border-dashed border-border-token bg-surface-sunken px-4 py-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={e => handleOcr(e.target.files?.[0] ?? null)}
                disabled={ocrBusy || result.kind === 'submitting'}
                // max-w-full: a file input is sized by the UA from its button
                // plus its filename label and does NOT stretch or shrink to its
                // container, so `block` alone leaves it free to grow past the
                // 245px it has here — empty it measures ~205px, but the moment
                // a nameplate photo with a real filename is picked it exceeds
                // that and gets clipped by the card's `overflow-hidden`.
                className="block max-w-full text-sm text-content file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-surface-raised file:border file:border-border-token file:text-sm file:text-content file:font-medium hover:file:bg-surface-sunken"
              />
              <p className="text-[11px] text-content-muted mt-1.5">
                Drops the serial and meter family into the form below — you can still edit them.
              </p>
              {ocrBusy && <p className="text-xs text-blue-700 dark:text-blue-300 mt-1.5 inline-flex items-center gap-1.5"><Spinner /> Reading nameplate…</p>}
              {ocrError && <p className="text-xs text-red-700 dark:text-red-400 mt-1.5">OCR failed: {ocrError}</p>}
              {ocr && (
                <div className="mt-2 text-xs text-content bg-surface-raised border border-border-token rounded p-2 flex flex-wrap gap-x-3 gap-y-1">
                  {ocr.meter_serial && <span><span className="text-content-muted">serial:</span> <span className="font-mono">{ocr.meter_serial}</span></span>}
                  {ocr.meter_type && <span><span className="text-content-muted">type:</span> {ocr.meter_type}</span>}
                  {ocr.manufacturer && <span><span className="text-content-muted">mfg:</span> {ocr.manufacturer}</span>}
                  {ocr.model && <span><span className="text-content-muted">model:</span> {ocr.model}</span>}
                </div>
              )}
            </div>
          </fieldset>

          {/* Step 1b — optional NFC tap (water meters only for v1) */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              1b. Tap meter via NFC (optional, water meters)
            </legend>
            {bridgeStatus === 'offline' ? (
              // The sentence is wrapped in ONE <span> so it stays ONE flex item.
              // Per CSS Flexbox L1 §4 every in-flow child of a flex container
              // becomes a flex item — inline children are blockified — and only
              // contiguous runs of bare TEXT get folded into anonymous items.
              // Unwrapped, this box was therefore a four-item `flex-nowrap`
              // row: the dot, "NFC bridge offline — install", the blockified
              // <code>, and "on this laptop to enable NFC reads." At 375px each
              // one shrinks and wraps independently, so the sentence rendered as
              // four ragged columns instead of a paragraph — the "laid out as if
              // in columns" in the report. (It never actually overflowed: 200.7px
              // min-content against a 275px box.) `items-start` + `mt-1` keeps
              // the 8px dot optically centred on the first 16px line box, which
              // is where `items-center` put it while the text was one line, so
              // 1440px is unchanged bar ~8px of gap the extra items used to add.
              <div className="rounded-lg border border-border-token bg-surface-sunken px-4 py-3 text-xs text-content-muted inline-flex items-start gap-2">
                <span className="mt-1 inline-block w-2 h-2 shrink-0 rounded-full bg-stone-500 dark:bg-stone-400" />
                <span className="min-w-0">
                  NFC bridge offline — install <code className="text-[11px]">utilsync-nfc-bridge</code> on this laptop to enable NFC reads.
                </span>
              </div>
            ) : bridgeStatus === 'unauthenticated' ? (
              // Actionable guidance (refresh + sign in), not passive status —
              // dark:bg-amber-900/dark:border-amber-800/dark:text-amber-100
              // (8.15:1, computed by hand; text-amber-800 alone is 1.67:1).
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 px-4 py-3 text-xs text-amber-800 dark:text-amber-100">
                NFC bridge rejected the session token — refresh the page and sign in again.
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/40 px-4 py-3">
                <button
                  type="button"
                  onClick={handleNfc}
                  disabled={nfcBusy || result.kind === 'submitting'}
                  className="text-sm font-medium inline-flex items-center gap-2 px-3 py-1.5 bg-surface-raised border border-emerald-300 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-950 disabled:opacity-50"
                >
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  {nfcBusy ? 'Tap meter against the reader…' : 'Read meter (NFC)'}
                </button>
                {/* text-emerald-700 alone is 2.77:1 against the dark card —
                    fails. dark:text-emerald-300 = 9.95:1, computed by hand. */}
                <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300 mt-1.5">
                  Confirms the chip UID + meter family. The serial still needs to come from the meter face for v1.
                </p>
                {nfcError && <p className="text-xs text-red-700 dark:text-red-400 mt-1.5">NFC failed: {nfcError}</p>}
                {nfcChipUid && (
                  // chip UID is an identifier the technician must be able to
                  // read to confirm the right chip was tapped. text-emerald-900
                  // alone would be 1.56:1 against a dark bg — fails.
                  // dark:bg-emerald-950/dark:border-emerald-800/
                  // dark:text-emerald-100 (13.36:1) restores it.
                  <p className="mt-2 text-xs text-emerald-900 dark:text-emerald-100 bg-white dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded px-2 py-1 font-mono">
                    chip_uid: {nfcChipUid}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          {/* Step 2 — meter type as visual cards */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              2. Meter type
            </legend>
            {/* One-up below `sm` — see AssetTypeCards for the 375px arithmetic. */}
            <AssetTypeCards value={assetType} onChange={setAssetType} />
          </fieldset>

          {/* Step 3 — serial */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              3. Meter serial
            </legend>
            <input
              type="text"
              value={meterSerial}
              onChange={e => setMeterSerial(e.target.value)}
              placeholder="e.g. 25008764"
              required
              className="block w-full rounded-md border-border-token text-sm font-mono shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="text-[11px] text-content-muted">Becomes the asset name in the UtilSync Portal.</p>
          </fieldset>

          {/* Step 4 — parent asset */}
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              4. Parent asset
            </legend>
            <div className="flex gap-2">
              <input
                type="text"
                value={parentId}
                onChange={e => setParentId(e.target.value)}
                placeholder="Asset id of the building or site"
                required
                // min-w-0 — the VirtualMeters.tsx:255 precedent. A text input's
                // automatic minimum size is its intrinsic `size=20` width, which
                // at text-sm font-mono is 168.4px; with the shrink-0 "Find"
                // button (80.1px) and the gap the row needs 256.6px against
                // 275px. It clears 375px by 18px and nothing narrower, and
                // `flex-1` still grows it at every width, so this costs nothing
                // at 1440px.
                className="block flex-1 min-w-0 rounded-md border-border-token text-sm font-mono shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowFormParentPicker(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-border-token bg-surface-raised rounded-md hover:bg-surface-sunken shrink-0"
                title="Search for a building / site by name"
              >
                <SearchIcon /> Find
              </button>
            </div>
            <p className="text-[11px] text-content-muted">
              The new meter is created as a child of this asset in the active realm.
            </p>
          </fieldset>

          {/* Step 5 — breaker size (electricity only). Drives Ruraflex /
              Drakenstein capacity charges. Hidden for water meters since they
              have no breaker. */}
          {ASSET_TYPES.find(t => t.value === assetType)?.meterFamily === 'electricity' && (
            <fieldset className="space-y-2">
              <legend className="text-xs font-semibold uppercase tracking-wide text-content-muted">
                5. Mains breaker (optional)
              </legend>
              <div className="flex gap-2">
                {/* min-w-0 for the same reason as step 4's input: this column's
                    automatic minimum is the number input's intrinsic 160.3px,
                    and with the phases <select> the row needs 248.6px of the
                    275px available. `flex-1` still drives the width at 1440px. */}
                <div className="flex-1 min-w-0">
                  <label className="block text-xs text-content-muted mb-1">Size (A)</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={breakerSizeAmps}
                    onChange={e => setBreakerSizeAmps(e.target.value)}
                    placeholder="e.g. 60"
                    className="block w-full rounded-md border-border-token text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-content-muted mb-1">Phases</label>
                  <select
                    value={breakerPhases}
                    onChange={e => setBreakerPhases(e.target.value as '1' | '3')}
                    className="block rounded-md border-border-token text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="3">3-phase</option>
                    <option value="1">1-phase</option>
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-content-muted">
                Drives Ruraflex / Drakenstein per-kVA capacity charges. Leave blank if unknown — can be set later.
              </p>
            </fieldset>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={result.kind === 'submitting' || !formValid}
              // bg-primary-600 + text-white -> bg-accent + text-accent-fg
              // (theme-aware); hover:bg-primary-700 -> hover:opacity-90 per
              // the established Button-variant convention (a static
              // hover:bg-primary-700 would leave near-invisible text against
              // accent's dark teal-400 value). disabled:bg-gray-300 ->
              // disabled:bg-border-token, dark-safe.
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:opacity-90 disabled:bg-border-token text-accent-fg text-sm font-medium rounded-md shadow-sm"
            >
              {result.kind === 'submitting' ? <><Spinner /> Creating asset…</> : <><PlugIcon /> Commission meter</>}
            </button>
            {result.kind !== 'idle' && (
              <button
                type="button"
                onClick={reset}
                className="px-3 py-2 border border-border-token text-content text-sm rounded-md hover:bg-surface-sunken"
              >
                Reset form
              </button>
            )}
          </div>

          {result.kind === 'success' && (
            // Terminal capture-state indicator (success) — same treatment as
            // ReadingPage's Submitted/ErrorBox. Fix-wave B: routed onto
            // ui/Alert (same bg-green-50/900, text-green-900/100 shape this
            // box already used — see fix-wave-b-report.md).
            <Alert tone="success" className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckIcon />
                <p className="font-semibold">Asset created in the UtilSync Portal.</p>
              </div>
              <p className="font-mono text-xs text-green-800/80 dark:text-green-100">asset_id: {result.asset_id}</p>
              {result.warnings.length > 0 && (
                // amber text nested inside the green Alert: amber-800
                // is 1.29:1 against green-900 — fails badly.
                // dark:text-amber-200 = 7.32:1, computed by hand.
                <ul className="text-xs text-amber-800 dark:text-amber-200 list-disc pl-5">
                  {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              {result.needs_cf_wiring && (
                <div className="text-xs text-amber-900 dark:text-amber-100 bg-amber-100 dark:bg-amber-900 border border-amber-200 dark:border-amber-800 rounded p-2 flex items-start gap-2">
                  <ClockIcon />
                  <span>
                    Follow-up: METERS_KV / ROUTES_KV / M2M_KV / Frontdoor still need to be wired
                    before the modem path delivers telemetry. Run{' '}
                    <code className="text-[11px]">scripts/openremote-create-consumer-asset.py</code>{' '}
                    against the new asset, then mark it as wired below.
                  </span>
                </div>
              )}
            </Alert>
          )}

          {result.kind === 'error' && (result.existing ? (
            // The serial is already committed: same message, plus Retire for
            // admins, then "Commission again" re-runs this same submit.
            <AlreadyCommissionedNotice
              message={result.message}
              existing={result.existing}
              onCommissionAgain={() => { void submit(); }}
              icon={<WarnIcon />}
            />
          ) : (
            // Terminal capture-state indicator (failure). Fix-wave B: routed
            // onto ui/Alert (same bg-red-50/900, text-red-800/100 shape this
            // box already used — see fix-wave-b-report.md).
            <Alert tone="danger" className="flex items-start gap-2">
              <WarnIcon /> <span>{result.message}</span>
            </Alert>
          ))}
        </form>
      </div>

      {showFormParentPicker && (
        <ParentPickerModal
          row={{
            id: '__form__',
            realm: '',           // empty realm = "all realms" search by default
            meter_serial: meterSerial || '(form)',
            asset_type: assetType,
            status: 'pending_review',
            source: 'manual',
            source_payload: null,
            image_r2_key: null,
            created_by: null,
            created_at: '',
            error: null,
          }}
          mode="form"
          busy={false}
          onCancel={() => setShowFormParentPicker(false)}
          onPick={(p) => {
            setParentId(p.asset_id);
            setShowFormParentPicker(false);
          }}
        />
      )}

      <PendingCommissioningList />

      <NeedsCfWiringList />
    </div>
  );
}

function readPayload(p: PendingCommissioning['source_payload']): {
  input_type?: string;
  building_hint?: string;
  client?: string;
  type_hint?: string | null;
  extracted?: Record<string, unknown>;
} {
  if (!p) return {};
  if (typeof p === 'string') {
    try { return JSON.parse(p); } catch { return {}; }
  }
  return p;
}

/**
 * The ASSET_TYPES value when `stored` is one of them, else null. Anything else
 * (billing-api's 'unknown' sentinel for a handed-off reading whose utility
 * can't be told, or any stray value) means no type has been chosen, so nothing
 * may be pre-selected.
 */
function supportedAssetType(stored: string): string | null {
  return ASSET_TYPES.some(t => t.value === stored) ? stored : null;
}

// Electricity / Water as visual cards: the manual form's step 2 and every
// pending review row. `compact` drops the schema sub-label for the row.
// `aria-pressed` carries the selection, which was otherwise colour-only.
//
// One-up below `sm`.
// -------------------------------------------------------------------------
// `grid-cols-2` is `repeat(2, minmax(0,1fr))`, so at 375px each track is
// (275 - 12) / 2 = 131.5px — but a track width is not a cap. Each card is a
// grid item with the default `min-width: auto`, so it is floored by its own
// content-based minimum: border-2 (8) + px-4 (32) + a `shrink-0` 20px icon +
// gap-3 (12) = 72px of chrome, plus the widest unbreakable label word.
// Measured against the real fonts: "Electricity" is 64.8px and "Groundwater"
// is 68.9px, giving 136.8px and 140.9px cards in 131.5px tracks. The row runs
// 289.7px against the form's 275px content box and eats two thirds of the
// card's right padding, while both sub-labels shred into a ~65px column.
// `min-w-0` on the inner div (already present) does not help: the blowout is
// the BUTTON's automatic minimum size, not the text div's.
//
// Two columns need a >=394px viewport to stop overflowing, so the gate is `sm`
// (640px, first breakpoint clear of it with margin — a 640px viewport gives
// 264px tracks). 1440px keeps the two-up layout exactly as it was. `min-w-0`
// on the button is the belt-and-braces half: it drops the automatic minimum
// so a grid item can never exceed its track at any width.
//
// (Those widths were measured when the page gutter was p-6; it is now
// p-4 lg:p-6, which only widens the box. A pending row's grid spans the row's
// content width, the same px-6-inside-the-card box as the form, and compact
// cards have the same 72px chrome with "Electricity" as the widest word — so
// the same gate holds there. Reasoned from the classes, not measured.)
function AssetTypeCards({
  value,
  onChange,
  compact = false,
  disabled = false,
}: {
  value: string | null;
  onChange: (v: string) => void;
  compact?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {ASSET_TYPES.map(t => {
        const selected = value === t.value;
        return (
          <button
            type="button"
            key={t.value}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(t.value)}
            className={`flex min-w-0 items-start gap-3 rounded-lg border-2 px-4 py-3 text-left transition disabled:opacity-50 ${
              selected
                // primary-* is a fixed literal ramp (teal), not a
                // CSS-var token — bg-primary-50/text-primary-900
                // would render as a near-invisible dark-on-dark
                // pairing on a dark card (1.60:1, computed by hand).
                // dark:bg-primary-900/dark:text-primary-100 (8.41:1
                // — the identical hex pair Badge's success tone
                // already uses) fixes it; dark:ring-primary-800 keeps
                // the selection ring from turning into a bright halo.
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900 ring-1 ring-primary-200 dark:ring-primary-800'
                : 'border-border-token bg-surface-raised hover:border-border-token hover:bg-surface-sunken'
            }`}
          >
            {t.meterFamily === 'electricity' ? <BoltIcon active={selected} /> : <DropletIcon active={selected} />}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${selected ? 'text-primary-900 dark:text-primary-100' : 'text-content'}`}>
                {t.meterFamily === 'electricity' ? 'Electricity' : 'Water'}
              </p>
              {!compact && <p className="text-[11px] text-content-muted mt-0.5 leading-snug">{t.label}</p>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PendingCommissioningList() {
  const [rows, setRows] = useState<PendingCommissioning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ row: PendingCommissioning; assetType: string } | null>(null);
  const [lastResolved, setLastResolved] = useState<string | null>(null);
  // Per-row Electricity/Water choice, keyed by record id. Absent means "use
  // the stored type if it is a supported one" (see supportedAssetType) — a
  // handed-off xlsx reading is stored as 'unknown' and starts with nothing
  // chosen, so the admin decides from the nameplate instead of the worker
  // guessing from {"cumulative": X}.
  const [chosenTypes, setChosenTypes] = useState<Record<string, string>>({});
  // Set when finalize refused because the serial is already committed; `retry`
  // repeats the exact finalize once the old commission is retired.
  const [blocked, setBlocked] = useState<{ message: string; existing: ExistingCommission; retry: () => void } | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.getPendingCommissioning();
      setRows(r.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function commitWith(row: PendingCommissioning, parent: ParentAssetMatch, assetType: string) {
    setActingId(row.id);
    setError(null);
    setBlocked(null);
    try {
      const resp = await api.finalizeCommissioning({
        meter_serial: row.meter_serial,
        // The type chosen on the row, not row.asset_type: the stored value
        // may be the 'unknown' sentinel or a pre-filled guess.
        asset_type: assetType,
        parent_id: parent.asset_id,
        source: 'email',
        pending_record_id: row.id,
      });
      if (resp.ok) {
        setRows(prev => prev.filter(r => r.id !== row.id));
        setPicker(null);
        setLastResolved(`Commissioned ${row.meter_serial} under ${parent.name} (${parent.realm}). asset_id=${resp.asset_id}`);
      } else {
        setError('Finalize did not return ok=true');
      }
    } catch (err) {
      const existing = alreadyCommissioned(err);
      if (existing) {
        setPicker(null);
        setBlocked({ message: (err as Error).message, existing, retry: () => { void commitWith(row, parent, assetType); } });
      } else {
        setError((err as Error).message);
      }
    } finally {
      setActingId(null);
    }
  }

  async function handleReject(row: PendingCommissioning) {
    const reason = window.prompt(`Reject commissioning request for ${row.meter_serial}?`, '');
    if (reason === null) return;
    setActingId(row.id);
    try {
      await api.rejectPendingCommissioning(row.id, reason || undefined);
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingId(null);
    }
  }

  return (
    // Not wrapped in <Card>: border-l-4 is a deliberate section-colour
    // stripe (indigo = email queue), same reasoning as the commission-form
    // card above.
    <div className="bg-surface-raised rounded-xl shadow overflow-hidden border-l-4 border-indigo-500">
      {/* dark:from-indigo-950/40 — same treatment as the primary-50/60
          header gradient above, using indigo's own dark shade so the wash
          stays a subtle tint instead of a bright patch. */}
      <div className="px-6 py-4 border-b border-border-token bg-gradient-to-r from-indigo-50/60 dark:from-indigo-950/40 to-transparent flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MailIcon />
            <h2 className="text-lg font-semibold text-content">Pending email submissions</h2>
            {rows.length > 0 && (
              // Self-contained solid fill (bg-indigo-600 + white text) — its
              // own contrast pair is unaffected by the page theme, same as
              // Pockets.tsx's summary dots. Not routed through Badge: this is
              // a notification-count bubble, not a status chip.
              <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 h-5 text-[11px] font-semibold rounded-full bg-indigo-600 text-white">
                {rows.length}
              </span>
            )}
          </div>
          {/* break-words on the address: `commission@utilsync.cloud` carries no
              UAX-14 break opportunity (no hyphen, no solidus — `@` and `.` are
              both non-breaking between letters), so it is a single 180.5px
              unbreakable token at text-xs mono. This column is only 172.8px at
              375px (card 323 - px-6 48 - gap 12 - the shrink-0 Refresh button),
              so it overflowed and was clipped by the card's `overflow-hidden`.
              `overflow-wrap: break-word` only splits a word that cannot fit, so
              1440px, where the column is ~900px, is untouched. */}
          <p className="text-sm text-content-muted mt-0.5">
            Meters submitted via <code className="text-xs break-words">commission@utilsync.cloud</code>{' '}
            (xlsx batch, photo, or structured body). Resolve a parent asset to finalize.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          // hover:bg-white is a bright "pop" against the tinted header —
          // fine in light, but a glaring flash on dark. dark:hover:bg-border-token
          // gives the same "lighter than its surroundings" pop using the
          // theme's own lightest-available neutral.
          className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content px-2.5 py-1.5 rounded hover:bg-white dark:hover:bg-border-token border border-transparent hover:border-border-token shrink-0"
        >
          <RefreshIcon /> Refresh
        </button>
      </div>

      {lastResolved && (
        <div className="px-6 py-3 bg-green-50 dark:bg-green-900 border-b border-green-200 dark:border-green-800 text-sm text-green-900 dark:text-green-100 flex items-start justify-between gap-4">
          <span className="inline-flex items-start gap-2"><CheckIcon /><span>{lastResolved}</span></span>
          <button onClick={() => setLastResolved(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100 font-bold" aria-label="Dismiss">×</button>
        </div>
      )}
      {error && (
        <div className="px-6 py-3 bg-red-50 dark:bg-red-900 border-b border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-100 inline-flex items-start gap-2">
          <WarnIcon /> <span>{error}</span>
        </div>
      )}
      {blocked && (
        <div className="px-6 py-3 border-b border-border-token">
          <AlreadyCommissionedNotice
            message={blocked.message}
            existing={blocked.existing}
            onCommissionAgain={blocked.retry}
            icon={<WarnIcon />}
          />
        </div>
      )}

      {loading ? (
        <div className="p-8 text-sm text-content-muted text-center inline-flex items-center justify-center gap-2 w-full">
          <Spinner /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-sm text-content-muted text-center">
          <div className="mx-auto mb-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-surface-sunken">
            <MailIcon />
          </div>
          <p>No pending email submissions.</p>
          <p className="text-xs text-content-muted mt-1">
            Field staff send to <code className="text-[11px]">commission@utilsync.cloud</code> with a photo, xlsx, or structured body.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border-token">
          {rows.map(row => {
            const payload = readPayload(row.source_payload);
            const buildingHint = payload.building_hint;
            const inputType = (payload.input_type as 'xlsx' | 'ocr' | 'body' | 'reading_handoff' | undefined) ?? undefined;
            const isHandoff = inputType === 'reading_handoff';
            const chosen = chosenTypes[row.id] ?? supportedAssetType(row.asset_type);
            return (
              // bg-indigo-50/40 row tint is a colour-only signal ("came from
              // reading@ handoff") — dark:bg-indigo-950/40 keeps it
              // distinguishable instead of washing out on the dark card.
              <li key={row.id} className={`px-6 py-3 hover:bg-surface-sunken ${isHandoff ? 'bg-indigo-50/40 dark:bg-indigo-950/40' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className="shrink-0 mt-0.5">
                    {chosen === null
                      ? <TypeNeededIcon />
                      : chosen === 'GroundwaterSensorAsset' ? <DropletIcon /> : <BoltIcon />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-mono text-sm font-medium text-content">{row.meter_serial}</p>
                      <RealmPill realm={row.realm} />
                      {isHandoff ? (
                        // No Badge tone maps to indigo — 'purple' per the
                        // same cross-group indigo->purple normalization used
                        // throughout this migration.
                        <Badge tone="purple" className="gap-1" title="Came from the reading@ pipeline — admin flagged as new meter">
                          ↻ from reading@
                        </Badge>
                      ) : (
                        <SourcePill mode={inputType as 'xlsx' | 'ocr' | 'body' | undefined} />
                      )}
                    </div>
                    {isHandoff && (
                      // text-indigo-800 fails against the dark card/tint —
                      // dark:text-indigo-300 (7.61:1, computed by hand).
                      <p className="text-[11px] text-indigo-800 dark:text-indigo-300 mt-1 italic">
                        Verify the nameplate before finalizing — this row was a reading for a serial that wasn't in the Portal.
                      </p>
                    )}
                    {buildingHint && (
                      <p className="text-xs text-content mt-1 inline-flex items-center gap-1.5">
                        <BuildingIcon />
                        <span className="text-content-muted">building hint:</span>
                        <span className="font-medium">{buildingHint}</span>
                      </p>
                    )}
                    <p className="text-[11px] text-content-muted mt-0.5">
                      {chosen && <>{chosen} · </>}from {row.created_by ?? '—'} ·{' '}
                      {parseDbTimestamp(row.created_at)?.toLocaleString() ?? row.created_at}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      disabled={actingId === row.id || chosen === null}
                      onClick={() => { if (chosen) setPicker({ row, assetType: chosen }); }}
                      title={chosen === null ? 'Choose Electricity or Water first' : undefined}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-accent text-accent-fg rounded-md hover:opacity-90 disabled:opacity-50"
                    >
                      <PlugIcon small /> Resolve
                    </button>
                    <button
                      type="button"
                      disabled={actingId === row.id}
                      onClick={() => handleReject(row)}
                      // Delete-trigger precedent (groups A/D/E): dark:border-red-800
                      // dark:text-red-400 (5.48:1) dark:hover:bg-red-950.
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50"
                      title="Reject this submission"
                    >
                      Reject
                    </button>
                  </div>
                </div>
                {/* The submitted photo or sheet, on its own line for the same
                    reason as the type cards below: the middle column above is
                    too narrow at 375px for another button. */}
                {row.image_r2_key && (
                  <div className="mt-2">
                    <DownloadOriginalButton kind="commissioning" id={row.id} />
                  </div>
                )}
                {/* Its own full-width line, not a flex item in the row above:
                    that row's middle column is what is left after the icon
                    and the shrink-0 Resolve/Reject pair, far too narrow for
                    two cards at 375px. */}
                <fieldset className="mt-3 space-y-1.5">
                  <legend className={`text-[11px] ${chosen === null ? 'font-medium text-content' : 'text-content-muted'}`}>
                    {chosen === null ? 'Meter type needed: choose Electricity or Water from the nameplate' : 'Meter type'}
                  </legend>
                  <AssetTypeCards
                    compact
                    value={chosen}
                    disabled={actingId === row.id}
                    onChange={(v) => setChosenTypes(prev => ({ ...prev, [row.id]: v }))}
                  />
                </fieldset>
              </li>
            );
          })}
        </ul>
      )}

      {picker && (
        <ParentPickerModal
          // The header names the type about to be built — the chosen one.
          row={{ ...picker.row, asset_type: picker.assetType }}
          busy={actingId === picker.row.id}
          onCancel={() => setPicker(null)}
          onPick={(p) => commitWith(picker.row, p, picker.assetType)}
        />
      )}
    </div>
  );
}

// The shared finder (components/ParentAssetPicker.tsx), configured from a
// commissioning row: realm toggle on for an email row with a realm, label by mode.
function ParentPickerModal({
  row,
  mode = 'pending',
  busy,
  onCancel,
  onPick,
}: {
  row: PendingCommissioning;
  mode?: 'pending' | 'form';
  busy: boolean;
  onCancel: () => void;
  onPick: (p: ParentAssetMatch) => void;
}) {
  return (
    <ParentAssetPicker
      description={mode === 'pending' ? (
        <p className="text-sm text-content-muted mt-0.5">
              Will commission <code className="bg-border-token px-1 rounded">{row.meter_serial}</code>{' '}
              ({row.asset_type}) under the chosen parent in realm{' '}
              <code className="bg-border-token px-1 rounded">{row.realm}</code>.
            </p>
          ) : (
            <p className="text-sm text-content-muted mt-0.5">
              Pick a parent asset (Building, Group, etc.). Drill in with ▸ when a meter
              should sit inside a group rather than directly under a building.
            </p>
      )}
      initialQuery={readPayload(row.source_payload).building_hint || ''}
      realm={row.realm || null}
      realmMode={mode === 'pending' && row.realm ? 'toggle-on' : 'toggle-off'}
      pickLabel={mode === 'pending' ? 'Commission here' : 'Use this'}
      busy={busy}
      onCancel={onCancel}
      onPick={onPick}
    />
  );
}

function NeedsCfWiringList() {
  const [rows, setRows] = useState<CommissioningRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listCommissioningRecords({ needsCfWiring: true });
      setRows(r.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function markWired(id: string) {
    setActingId(id);
    try {
      await api.markCommissioningCfWired(id);
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingId(null);
    }
  }

  return (
    // Not wrapped in <Card>: border-l-4 is a deliberate section-colour
    // stripe (amber = needs-wiring), same reasoning as the other two lists.
    <div className="bg-surface-raised rounded-xl shadow overflow-hidden border-l-4 border-amber-500">
      {/* dark:from-amber-950/40 — same treatment as the primary/indigo header
          gradients above. */}
      <div className="px-6 py-4 border-b border-border-token bg-gradient-to-r from-amber-50/60 dark:from-amber-950/40 to-transparent flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ClockIcon />
            <h2 className="text-lg font-semibold text-content">Awaiting Cloudflare wiring</h2>
            {rows.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 h-5 text-[11px] font-semibold rounded-full bg-amber-500 text-white">
                {rows.length}
              </span>
            )}
          </div>
          <p className="text-sm text-content-muted mt-0.5">
            Commissioned meters whose KV / Frontdoor / modem registration still needs a human.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content px-2.5 py-1.5 rounded hover:bg-white dark:hover:bg-border-token border border-transparent hover:border-border-token shrink-0"
        >
          <RefreshIcon /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="p-8 text-sm text-content-muted text-center inline-flex items-center justify-center gap-2 w-full">
          <Spinner /> Loading…
        </div>
      ) : error ? (
        // text-red-700 alone is 2.34:1 against the dark card — fails.
        // dark:text-red-400 = 5.48:1, computed by hand.
        <div className="p-6 text-sm text-red-700 dark:text-red-400 inline-flex items-start gap-2"><WarnIcon /> <span>{error}</span></div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-sm text-content-muted text-center">
          <div className="mx-auto mb-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-surface-sunken">
            <CheckIcon />
          </div>
          <p>Nothing waiting.</p>
          <p className="text-xs text-content-muted mt-1">New commissionings appear here for follow-up.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border-token">
          {rows.map(row => {
            const isWater = row.asset_type === 'GroundwaterSensorAsset';
            return (
              <li key={row.id} className="px-6 py-3 flex items-center gap-3 hover:bg-surface-sunken">
                <div className="shrink-0">
                  {isWater ? <DropletIcon /> : <BoltIcon />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono text-sm font-medium text-content">{row.meter_serial}</p>
                    <RealmPill realm={row.realm} />
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-border-token text-content">{row.asset_type}</span>
                  </div>
                  <p className="text-[11px] text-content-muted mt-0.5 font-mono break-all">
                    asset_id: {row.asset_id ?? '—'}
                  </p>
                  <p className="text-[11px] text-content-muted mt-0.5">
                    Committed{' '}
                    {row.committed_at
                      ? parseDbTimestamp(row.committed_at)?.toLocaleString() ?? row.committed_at
                      : '—'}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={actingId === row.id}
                  onClick={() => markWired(row.id)}
                  // text-amber-800 fails against its own bg-amber-50 once that
                  // gains a dark fill (1.67:1 against amber-900, computed by
                  // hand). dark:bg-amber-900/dark:border-amber-800/
                  // dark:text-amber-100 restores it; dark:hover:bg-amber-800
                  // keeps a visible hover step.
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 text-amber-800 dark:text-amber-100 rounded-md hover:bg-amber-100 dark:hover:bg-amber-800 disabled:opacity-50"
                >
                  {actingId === row.id ? <Spinner /> : <CheckIcon />}
                  {actingId === row.id ? 'Marking…' : 'Mark CF-wired'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visual helpers — small inline SVGs and pills used across the page
// ---------------------------------------------------------------------------

function PlugIcon({ small }: { small?: boolean } = {}) {
  const size = small ? 14 : 18;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current shrink-0" aria-hidden="true">
      <path d="M9 2v6M15 2v6" />
      <path d="M6 8h12v4a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8z" />
      <path d="M12 18v4" />
    </svg>
  );
}

// Neutral "no type chosen yet" mark for a pending row whose stored type is not
// a supported one. Unlike the decorative icons it is the only visual cue in
// that slot, so it is labelled rather than aria-hidden.
function TypeNeededIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-content-muted shrink-0" role="img" aria-label="Type needed">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function BoltIcon({ active = false }: { active?: boolean } = {}) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-amber-500 shrink-0' : 'text-amber-500/80 shrink-0'} aria-hidden="true">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function DropletIcon({ active = false }: { active?: boolean } = {}) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-sky-500 shrink-0' : 'text-sky-500/80 shrink-0'} aria-hidden="true">
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-500 shrink-0" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 6L2 7" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current shrink-0" aria-hidden="true">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function CheckIcon() {
  return (
    // dark:text-green-400: this icon is reused both on the plain dark card
    // (empty states) and as a leading glyph inside the green-900 success
    // box above — green-600 alone is borderline on the card (4.60:1) and
    // fails inside the box (2.76:1, computed by hand). green-400 clears both
    // (8.71:1 / 5.23:1).
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 dark:text-green-400 shrink-0" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current shrink-0 mt-0.5" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ClockIcon() {
  return (
    // Same multi-context reasoning as CheckIcon: amber-600 fails inside the
    // amber-900 box (2.85:1) though it clears the plain card (4.76:1).
    // dark:text-amber-400 (9.09:1 / 5.43:1, computed by hand) covers both.
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// Routed through ui/Badge. xlsx (emerald) -> 'success': Badge's success tone
// IS Tailwind teal, and emerald/teal are the same green-family hue this
// migration has normalized to 'success' everywhere else (e.g. AuditLogs'
// 'gazette' action). ocr (sky) -> 'info': Badge's info tone is literally
// sky-based — exact hue match. body (slate) -> 'neutral': same slate->stone
// normalization as RealmPill (components/ParentAssetPicker.tsx).
const SOURCE_TONE: Record<'xlsx' | 'ocr' | 'body', BadgeTone> = {
  xlsx: 'success',
  ocr: 'info',
  body: 'neutral',
};

function SourcePill({ mode }: { mode: 'xlsx' | 'ocr' | 'body' | undefined }) {
  if (!mode) return null;
  const labels: Record<string, string> = {
    xlsx: 'xlsx batch',
    ocr: 'photo OCR',
    body: 'email body',
  };
  return (
    <Badge tone={SOURCE_TONE[mode]}>
      {labels[mode]}
    </Badge>
  );
}
